import type { MVFProfile } from "@lib/tranche";
import { isLessThanOrEqualWithinFloatingPointTolerance } from "@lib/utils/utils";
import mapValues from "lodash/mapValues";
import { rescaleMVF } from "./meanRecommender";

function getHighestMvfPointValue(mvfProfile: MVFProfile): number {
  return Math.max(
    0,
    ...Object.values(mvfProfile).map((mvf) => mvf[0]?.value ?? 0),
  );
}

export function isMvfProfileNotNormalized(mvfProfile: MVFProfile): boolean {
  const highestMvfPointValue = getHighestMvfPointValue(mvfProfile);
  const highestPointIsOne =
    isLessThanOrEqualWithinFloatingPointTolerance(highestMvfPointValue, 1) &&
    isLessThanOrEqualWithinFloatingPointTolerance(1, highestMvfPointValue);
  return highestMvfPointValue > 0 && !highestPointIsOne;
}

export default function normalizeMvfProfile(
  mvfProfile: MVFProfile,
): MVFProfile {
  const highestFdv = getHighestMvfPointValue(mvfProfile);
  // With nothing to scale against (e.g. an all-zero / all-setToZero profile),
  // fall back to a multiplier of 1 rather than dividing by zero and emitting
  // NaN-valued MVFs. We still run rescaleMVF so the result is always a fresh
  // copy — callers like getMeanRecommendationsInner delete keys from the
  // returned profile and must not alias the input's `recommender.mvfProfile`.
  const multiplier = highestFdv > 0 ? 1 / highestFdv : 1;
  return mapValues(mvfProfile, (mvf) => rescaleMVF(mvf, multiplier));
}
